import { useMonarchRule } from '../../MonarchRulesProvider';
import {
  CheckoutResult,
  CheckoutVariant,
  MultiCurrency,
  UseCheckoutTestHookResult,
} from './types';

const CUSTOM_RULE_ID = 'checkout';

// Stable reference so the mapped multiCurrency object doesn't churn memoized
// consumers when Monarch omits acceptedCurrencies.
const EMPTY_ACCEPTED_CURRENCIES: readonly string[] = [];

type CheckoutProps = {
  showAlternateView: boolean;
  submitOrderCtaText: string;
  promoCodeCtaText: string;
  formTitleText: string;
  couponTitleText: string;
  cartTitleText: string;
  payPalTitleText: string;
  couponTitleTooltip?: string;
  showStepper: boolean;
  showMobileLogo: boolean;
  shouldApplyAutoPromo?: boolean;
  autoPromoDiscountCode?: string;
  useShortAddressForm?: boolean;
  viewStepperCheckout?: boolean;
  priceText?: string;
  multiStepTemplate?: {
    stepIndicatorText?: string;
    nextButtonText?: string;
    step1Header?: string;
    step2Header?: string;
    personalInformationAccordionTitle?: string;
    paymentInformationAccordionTitle?: string;
  };
  discountsAppliedTexts?: {
    bundleDiscount?: string;
    couponDiscount?: string;
  };
  verifiedCertificateText?: string;
  certificatesBadgeTexts?: {
    singleCourseBadge?: string;
    programBadgeText?: string;
  };
  multiCurrency?: MultiCurrency;
  shouldAutoFillPii?: boolean;
  showMobileMinimal?: boolean;
  showUpi?: boolean;
  minimalDesignOptions?: {
    placeOrderBehavior?: 'alwaysEnabled' | 'requireAllFields';
    ctaBackgroundColor?: 'dark' | 'light';
    couponPlacement?: 'belowCartSummary' | 'belowPaymentInfo';
  };
};

const useCheckout: UseCheckoutTestHookResult = () => {
  // Call the Monarch API to get the experiment result
  const { isLoading, value } = useMonarchRule<{
    // https://admin.monarch.cohesionapps.com/sources/bdac7c16-924e-4180-bb35-5b2dfff6d308/content/native-returns/5b356d7c-c754-4a61-9bf4-6065a25db4b0/
    data: CheckoutProps;
  }>(CUSTOM_RULE_ID, {});

  // If still loading or no value returned, return loading state
  if (isLoading || !value) {
    return {
      isLoading,
    } as CheckoutResult;
  }

  // Determine the variant based on the returned data (NOTE: this will have to be swapped when we flip the experiment)
  const variant: CheckoutVariant = value?.data?.viewStepperCheckout
    ? 'stepper'
    : 'control';

  // Return the experiment result with proper typing
  return {
    isLoading: false,
    variant,
    data: {
      showAlternateView: value.data.showAlternateView,
      submitOrderCtaText: value.data.submitOrderCtaText,
      promoCodeCtaText: value.data.promoCodeCtaText,
      formTitleText: value.data.formTitleText,
      couponTitleText: value.data.couponTitleText,
      cartTitleText: value.data.cartTitleText,
      payPalTitleText: value.data.payPalTitleText,
      couponTitleTooltip: value.data?.couponTitleTooltip,
      showStepper: value.data?.showStepper ?? false,
      showMobileLogo: value.data?.showMobileLogo ?? true,
      shouldApplyAutoPromo: value.data?.shouldApplyAutoPromo ?? false,
      autoPromoDiscountCode: value.data?.autoPromoDiscountCode,
      useShortAddressForm: value.data?.useShortAddressForm ?? false,
      priceText: value.data?.priceText,
      discountsAppliedTexts: {
        bundleDiscount: value.data?.discountsAppliedTexts?.bundleDiscount,
        couponDiscount: value.data?.discountsAppliedTexts?.couponDiscount,
      },
      verifiedCertificateText: value.data?.verifiedCertificateText,
      certificatesBadgeTexts: value.data?.certificatesBadgeTexts,
      multiStepTemplate: {
        step1Header: value.data?.multiStepTemplate?.step1Header,
        step2Header: value.data?.multiStepTemplate?.step2Header,
        nextButtonText: value.data?.multiStepTemplate?.nextButtonText,
        personalInformationAccordionTitle:
          value.data?.multiStepTemplate?.personalInformationAccordionTitle,
        paymentInformationAccordionTitle:
          value.data?.multiStepTemplate?.paymentInformationAccordionTitle,
      },
      multiCurrency: {
        enableMultiCurrency: value.data?.multiCurrency?.enableMultiCurrency,
        enableIpCurrencyDetection:
          value.data?.multiCurrency?.enableIpCurrencyDetection,
        selectorLabel: value.data?.multiCurrency?.selectorLabel,
        acceptedCurrencies:
          value.data?.multiCurrency?.acceptedCurrencies ??
          EMPTY_ACCEPTED_CURRENCIES,
        content: {
          buttonText: value.data?.multiCurrency?.content?.buttonText,
          description: value.data?.multiCurrency?.content?.description,
          headline: value.data?.multiCurrency?.content?.headline,
        },
        multiCurrencyTooltip: value.data?.multiCurrency?.multiCurrencyTooltip,
      },
      shouldAutoFillPii: value.data?.shouldAutoFillPii,
      shouldShowMobileMinimal: value.data?.showMobileMinimal,
      showUpi: value.data?.showUpi ?? false,
      minimalDesignOptions: value.data?.minimalDesignOptions,
    },
  };
};

export default useCheckout;
